import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  ChevronDown,
  Check,
  Plus,
  Settings,
  Users,
} from "lucide-react";
import api from "../services/api";
import { setCurrentOrganizationId } from '../services/organizationContext';
import CreateOrganizationModal from "./CreateOrganizationModal";
import { toast } from './Toast';
import logger from '../utils/logger';
import "./CreateOrganizationModal.css";

const OrganizationSwitcher = () => {
  const [organizations, setOrganizations] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load organizations
  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await api.get("/organizations");
      setOrganizations(response.data);

      // Set current organization (most recently accessed)
      const active = response.data.find((org) => org.lastAccessedAt);
      if (active) {
        setCurrentOrg(active);
        setCurrentOrganizationId(active._id);
      } else if (response.data.length > 0) {
        setCurrentOrg(response.data[0]);
        setCurrentOrganizationId(response.data[0]._id);
      }
    } catch (error) {
      logger.apiError('/organizations', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const switchOrganization = async (org) => {
    try {
      await api.post(`/organizations/${org._id}/switch`);
      setCurrentOrg(org);
      setCurrentOrganizationId(org._id);
      setIsOpen(false);

      // Reload page to refresh data for new organization
      window.location.reload();
    } catch (error) {
      logger.apiError(`/organizations/${org._id}/switch`, error);
      toast.error('Failed to switch organization');
    }
  };

  const handleCreateSuccess = (newOrg) => {
    // Close modal
    setShowCreateModal(false);

    // Switch to new organization
    switchOrganization(newOrg);
  };

  const getTierBadgeColor = (tier) => {
    const colors = {
      free: "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300",
      starter: "bg-brand-100 dark:bg-brand-900/30 text-brand-800 dark:text-brand-300",
      professional: "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300",
      enterprise: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300",
    };
    return colors[tier] || colors.free;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      owner: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300",
      admin: "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300",
      manager: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300",
      user: "bg-brand-100 dark:bg-brand-900/30 text-brand-800 dark:text-brand-300",
      viewer: "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300",
    };
    return colors[role] || colors.user;
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-card rounded-lg border border-border">
        <Building2 className="w-5 h-5 text-muted-foreground animate-pulse" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (!currentOrg) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Current Organization Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-card rounded-lg border border-border hover:bg-muted transition-colors"
      >
        <Building2 className="w-5 h-5 text-muted-foreground" />
        <div className="flex flex-col items-start min-w-0">
          <span className="text-sm font-medium text-foreground truncate max-w-[150px]">
            {currentOrg.name}
          </span>
          <span className="text-xs text-muted-foreground capitalize">
            {typeof currentOrg.role === 'object' ? currentOrg.role?.name : currentOrg.role}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground transition-transform ${
            isOpen ? "transform rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-card rounded-lg shadow-lg border border-border z-50">
          {/* Organizations List */}
          <div className="p-2">
            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
              Your Organizations
            </div>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {organizations.map((org) => (
                <button
                  key={org._id}
                  onClick={() => switchOrganization(org)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors ${
                    currentOrg._id === org._id ? "bg-brand-50 dark:bg-brand-900/30" : ""
                  }`}
                >
                  <div className="flex-shrink-0">
                    <Building2
                      className={`w-5 h-5 ${
                        currentOrg._id === org._id
                          ? "text-brand-600 dark:text-brand-400"
                          : "text-gray-400 dark:text-gray-500"
                      }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">
                        {org.name}
                      </span>
                      {currentOrg._id === org._id && (
                        <Check className="w-4 h-4 text-brand-600 dark:text-brand-400 flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(
                          typeof org.role === 'object' ? org.role?.name : org.role
                        )}`}
                      >
                        {typeof org.role === 'object' ? org.role?.name : org.role}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${getTierBadgeColor(
                          org.subscription?.tier
                        )}`}
                      >
                        {org.subscription?.tier || "free"}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-border"></div>

          {/* Actions */}
          <div className="p-2 space-y-1">
            <Link
              to="/settings/organization"
              className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-lg transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <Settings className="w-4 h-4" />
              Organization Settings
            </Link>
            <Link
              to="/settings/organization/members"
              className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-lg transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <Users className="w-4 h-4" />
              Manage Members
            </Link>
            <button
              onClick={() => {
                setIsOpen(false);
                setShowCreateModal(true);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Organization
            </button>
          </div>
        </div>
      )}

      {/* Create Organization Modal */}
      <CreateOrganizationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default OrganizationSwitcher;
